import { useMemo } from "react";
import { notEmpty, toDate } from "@orb-labs/types";
import { Timeframe } from "../bullrun/enums.js";
/**
 * Backend stores prices as BigInt scaled by 1e6 (6 decimal places).
 * This divisor converts back to real dollar values.
 */
export const BIGINT_SCALE_FACTOR = 1e6;
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
/**
 * Chart query lookback per UI timeframe.
 * Keep in sync with `lookbackMs` in `domains/common/src/providers/data/tokens/types.ts`
 * (`CHART_TIMEFRAME_CONFIGS`).
 */
export const TIMEFRAME_LOOKBACK_MS = {
    [Timeframe.FIVE_MINUTES]: 5 * MINUTE,
    [Timeframe.ONE_HOUR]: HOUR,
    [Timeframe.FOUR_HOURS]: 4 * HOUR,
    [Timeframe.ONE_DAY]: DAY,
    [Timeframe.ONE_WEEK]: 7 * DAY,
    [Timeframe.ONE_MONTH]: 30 * DAY,
    [Timeframe.SIX_MONTHS]: 180 * DAY,
    [Timeframe.ONE_YEAR]: 365 * DAY,
    [Timeframe.MAX]: 365 * DAY,
    [Timeframe.ALL]: 365 * DAY,
};
function medianPositive(values) {
    const valid = values.filter((value) => Number.isFinite(value) && value > 0);
    if (valid.length === 0)
        return undefined;
    const sorted = [...valid].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
/** Lightweight-charts priceFormat tuned for token prices from sub-cent to thousands. */
export function chartPriceFormatOptions(prices) {
    const positive = prices.filter((price) => Number.isFinite(price) && price > 0);
    const reference = medianPositive(positive) ?? 1;
    if (reference >= 1000) {
        return { type: "price", precision: 2, minMove: 0.01 };
    }
    if (reference >= 1) {
        return { type: "price", precision: 4, minMove: 0.0001 };
    }
    if (reference >= 0.01) {
        return { type: "price", precision: 6, minMove: 0.000001 };
    }
    const exponent = Math.floor(Math.log10(reference));
    const precision = Math.min(12, Math.max(6, -exponent + 2));
    const minMove = 10 ** -precision;
    return { type: "price", precision, minMove };
}
/**
 * Smooth a time series using a centered simple moving average.
 *
 * - Window size adapts to data length (~4% of points, odd, capped at `maxWindow`).
 * - First and last points are pinned so start/end values stay exact.
 * - Near the edges the window shrinks symmetrically (no lag).
 *
 * This removes high-frequency jitter (e.g. stablecoin micro-fluctuations)
 * while preserving the overall trend for volatile assets.
 */
export function smoothTimeSeries(data, maxWindow = 9) {
    if (data.length <= 3)
        return data;
    const raw = Math.max(3, Math.floor(data.length / 25));
    const window = Math.min(maxWindow, raw % 2 === 0 ? raw + 1 : raw);
    const half = Math.floor(window / 2);
    return data.map((point, i) => {
        if (i === 0 || i === data.length - 1)
            return point;
        const start = Math.max(0, i - half);
        const end = Math.min(data.length - 1, i + half);
        const count = end - start + 1;
        let sum = 0;
        for (let j = start; j <= end; j++) {
            sum += data[j].value;
        }
        return { timestamp: point.timestamp, value: sum / count };
    });
}
/**
 * Compute evenly-spaced "nice" tick values that bracket [min, max].
 * First tick is at or below min, last tick is at or above max,
 * so the topmost data is never above the highest label.
 */
export function computeNiceTicks(min, max, targetCount = 5) {
    const range = max - min;
    if (range === 0)
        return [min];
    const roughStep = range / (targetCount - 1);
    const mag = 10 ** Math.floor(Math.log10(roughStep));
    const normalized = roughStep / mag;
    let niceStep;
    if (normalized <= 1)
        niceStep = mag;
    else if (normalized <= 2)
        niceStep = 2 * mag;
    else if (normalized <= 5)
        niceStep = 5 * mag;
    else
        niceStep = 10 * mag;
    const first = Math.floor(min / niceStep) * niceStep;
    const last = Math.ceil(max / niceStep) * niceStep;
    const ticks = [];
    for (let v = first; v <= last + niceStep * 0.001; v += niceStep) {
        ticks.push(Math.round(v * 1e10) / 1e10);
    }
    if (ticks.length < 2 && targetCount < 10) {
        return computeNiceTicks(min, max, targetCount + 2);
    }
    return ticks;
}
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Format a timestamp for a chart X-axis label.
 * Picks the right level of detail based on the total visible time span:
 *   ≤ 1 day  → "2:30 PM"
 *   ≤ 1 week → "Mon"
 *   ≤ 3 months → "Jan 15"
 *   > 3 months → "Jan '26"
 */
export function formatXAxisTimestamp(timestamp, totalDurationMs) {
    const date = new Date(timestamp);
    if (totalDurationMs <= 1.5 * DAY_MS) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const h12 = hours % 12 || 12;
        return `${h12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    }
    if (totalDurationMs <= 7 * DAY_MS) {
        return SHORT_DAYS[date.getDay()];
    }
    if (totalDurationMs <= 90 * DAY_MS) {
        return `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;
    }
    const yearShort = date.getFullYear().toString().slice(-2);
    return `${SHORT_MONTHS[date.getMonth()]} '${yearShort}`;
}
/**
 * Format a price value with adaptive decimal precision for chart overlays.
 *   ≥ 1000 → locale-formatted with 1 decimal (e.g. "66,312.4")
 *   ≥ 1    → 2 decimals (e.g. "1.23")
 *   < 1    → 4 significant digits (e.g. "0.004512")
 */
export function formatChartPrice(value) {
    if (value >= 1000)
        return value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    if (value >= 1)
        return value.toFixed(2);
    return value.toPrecision(4);
}
/**
 * Transforms MarketDataTimeSeriesAndSummary price data into chart-friendly points.
 * Divides values by 1e6 since the backend stores fiat amounts as BigInt with 6 decimal places.
 * Applies a light moving-average smooth to reduce high-frequency jitter.
 */
export function useLineChartData(data) {
    return useMemo(() => {
        if (!data?.price || data.price.length === 0)
            return [];
        const scaled = data.price
            .map((point) => {
            if (!notEmpty(point) || !notEmpty(point.value))
                return undefined;
            const ts = toDate(point.timestamp)?.getTime();
            if (!notEmpty(ts))
                return undefined;
            return { timestamp: ts, value: Number(point.value) / BIGINT_SCALE_FACTOR };
        })
            .filter(notEmpty);
        return smoothTimeSeries(scaled);
    }, [data]);
}
/**
 * Determines chart line color based on price direction (first vs last value).
 */
export function useLineColor(lineChartData, positiveColor, negativeColor) {
    return useMemo(() => {
        if (lineChartData.length < 2)
            return positiveColor;
        const firstValue = lineChartData[0].value;
        const lastValue = lineChartData[lineChartData.length - 1].value;
        return lastValue >= firstValue ? positiveColor : negativeColor;
    }, [lineChartData, positiveColor, negativeColor]);
}
